import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import { useConference } from '../contexts/ConferenceContext';
import { dayRuns, LIVE_HASH, useVenueNow, venueNow } from './program/liveProgram';
import { JumpButton } from './program/programStyles';

const API = process.env.REACT_APP_API_URL || '';

// How many days around the main conference's date (its dateTime, which need not be its first day) its
// program is checked at all, so that the site asks for it only around the conference; whether today is
// one of its days comes from the program itself.
const LOOK_DAYS = 30;

const dayOf = (dateTime) => (dateTime ? String(dateTime).slice(0, 10) : null);
const plusDays = (day, n) => {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

// Whether the program runs at `now` (the venue's day and minute): today is one of its days and the
// first slot of today has begun, until the last one ends.
export const programIsLive = (program, now) => {
  if (!program || !now) return false;
  const day = (program.days || []).find((d) => d.day === now.day);
  if (!day) return false;
  return dayRuns(program, day, now.minutes);
};

// The pages that do not offer it: the main conference's own page, the admin area and the print copy of
// the program.
const hiddenOn = (pathname, slug) => pathname.startsWith('/admin')
  || /\/program\/print\/?$/.test(pathname)
  || pathname.replace(/\/$/, '') === `/events/${slug}`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.8); }
  70% { box-shadow: 0 0 0 6px rgba(255, 255, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
`;

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: white;
  animation: ${pulse} 2s ease-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// On phones, while the main conference runs (venue time, liveProgram.js): a small button at the bottom
// right of every other page that leads to its program, opened at today ("Live: zum Programm"). Wider
// screens do not show it (JumpButton). The program is only asked for within LOOK_DAYS of the
// conference's date.
const LiveProgramButton = () => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const { conference } = useConference() || {};
  const slug = conference?.slug;
  const start = dayOf(conference?.dateTime);
  const timeZone = 'Europe/Vienna';
  const today = venueNow(timeZone).day;
  const near = Boolean(slug && start && plusDays(start, -LOOK_DAYS) <= today && today <= plusDays(start, LOOK_DAYS));
  const [program, setProgram] = useState(null);
  const now = useVenueNow(program?.event?.timeZone, near);

  useEffect(() => {
    if (!near) return undefined;
    let cancelled = false;
    axios.get(`${API}/api/public/events/${encodeURIComponent(slug)}/program`)
      .then((r) => { if (!cancelled) setProgram(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [near, slug]);

  if (!near || hiddenOn(pathname, slug) || !programIsLive(program, now)) return null;
  return (
    <JumpButton
      as={Link}
      to={`/events/${slug}${LIVE_HASH}`}
      aria-label={intl.formatMessage({ id: 'program.live.goto', defaultMessage: 'The congress is live: go to the program' })}
    >
      <Dot aria-hidden="true" />
      {intl.formatMessage({ id: 'program.live.button', defaultMessage: 'Live now: program' })}
    </JumpButton>
  );
};

export default LiveProgramButton;
